import {
  AndroidLogo,
  AppleLogo,
  CaretDoubleRight,
} from "@phosphor-icons/react";
import { Row } from "@tanstack/react-table";
import { Button, Card, Tooltip } from "flowbite-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Table } from "../../../components/Table";
import { useDateRange } from "../../../contexts/DateRangeContext";
import { useAuth } from "../../../hooks";
import { useAppListData } from "../../../hooks/get/useAppListData";
import { checkPermission, formatNumber } from "../../../lib/utils";
import { PERMISSION_MAPPER } from "../../../shared/constants";
import AppLogo from "../../AppCountryReports/components/AppLogo";
import type { IAdmobApp } from "../../Apps/interface";

interface TopPerformingAppChartProps {
  showWithTax?: boolean;
}

function TopPerformingAppChart({
  showWithTax = false,
}: TopPerformingAppChartProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dateRange } = useDateRange();
  const { data, isLoading } = useAppListData({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    per_page: 5,
  });

  const topApps = data?.results?.admob_apps || [];

  const hasAppDetailsReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_FIND_ONE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const clickableClassesForApp = useMemo(() => {
    return hasAppDetailsReadPermission
      ? "cursor-pointer transition-colors ease hover:text-primary-600"
      : "";
  }, [hasAppDetailsReadPermission]);

  // Define columns for Table.tsx
  const columns = [
    {
      id: "app_name",
      accessorKey: "app_name",
      header: t("apps.columns.name"),
      size: 250,
      minSize: 200,
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const app = row.original;
        return (
          <div
            className={`flex items-center relative gap-2 sm:gap-3 ${clickableClassesForApp}`}
            onClick={
              clickableClassesForApp
                ? () => navigate(`/apps/${app.app_slug}`)
                : undefined
            }
          >
            <div>
              {app?.platform?.toLowerCase() === "android" ? (
                <AndroidLogo weight="fill" className="w-4 h-4 text-green-400" />
              ) : app?.platform?.toLowerCase() === "ios" ? (
                <AppleLogo weight="fill" className="w-4 h-4 text-gray-700" />
              ) : null}
            </div>
            <div className="min-w-10 w-10 min-h-10 h-10 flex justify-center items-center border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <AppLogo
                logoUrl={app.logo_url}
                platform={app.platform}
                appName={app.app_name}
              />
            </div>
            <p className="text-sm font-medium break-words line-clamp-3">
              {app.app_name}
            </p>
          </div>
        );
      },
    },
    {
      id: "estimate_revenue",
      header: t("apps.columns.estimatedRevenue"),
      accessorKey: "estimate_revenue",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const value = row.original.estimate_revenue;
        return (
          <span className="text-sm whitespace-nowrap text-right block">
            ${isNaN(value) ? "0.00" : formatNumber(value)}
          </span>
        );
      },
      size: 150,
      meta: { headerAlign: "right" as const },
    },
    {
      id: "without_tax.total_cost_usd",
      header: t("apps.columns.totalCostDollar"),
      accessorKey: "without_tax.total_cost_usd",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const app = row.original;
        const value = app.without_tax.total_cost_usd;
        const valueWithTax = app.with_tax?.total_cost_usd;
        return (
          <div className="flex flex-col items-end relative">
            <span className="text-sm">
              ${isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
            {showWithTax && (
              <Tooltip content="With Tax">
                <span className="text-xs text-gray-500 mt-1">
                  ${isNaN(valueWithTax) ? "0.00" : formatNumber(valueWithTax)}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
      size: 120,
      meta: { headerAlign: "right" as const },
    },
    {
      id: "without_tax.total_cost_usd_net",
      header: t("apps.columns.netDollar"),
      accessorKey: "without_tax.total_cost_usd_net",
      cell: ({ row }: { row: Row<IAdmobApp> }) => {
        const app = row.original;
        const value = app.without_tax.total_cost_usd_net;
        const valueWithTax = app.with_tax?.total_cost_usd_net;
        return (
          <div className="flex flex-col items-end relative">
            <span className="text-sm">
              ${isNaN(value) ? "0.00" : formatNumber(value)}
            </span>
            {showWithTax && (
              <Tooltip content="With Tax">
                <span className="text-xs text-gray-500 mt-1">
                  ${isNaN(valueWithTax) ? "0.00" : formatNumber(valueWithTax)}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
      size: 120,
      meta: { headerAlign: "right" as const },
    },
  ];

  return (
    <Card
      theme={{
        root: {
          base: "flex rounded-lg border border-gray-200 bg-white  dark:border-gray-700 dark:bg-gray-800",
          children: "flex h-full flex-col justify-center gap-4 p-4",
        },
      }}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <AndroidLogo size={20} className="text-gray-700" weight="duotone" />
          <h1 className="text-md font-bold text-gray-700">
            {t("dashboard.topApps.title")}
          </h1>
        </div>
        <Button
          size="xs"
          outline
          color="indigo"
          onClick={() => navigate("/apps")}
        >
          <div className="flex items-center gap-1">
            {t("common.seeAllApps")}
            <CaretDoubleRight size={16} weight="duotone" />
          </div>
        </Button>
      </div>
      <div className="overflow-auto h-full">
        <Table
          data={topApps}
          columns={columns}
          isLoading={isLoading}
          showPagination={false}
          loadingHeight={4}
          loadingRowCount={5}
          sortByKey="estimate_revenue"
          sortOrder="desc"
          className="h-full"
        />
      </div>
    </Card>
  );
}

export default TopPerformingAppChart;
